<template>
    <div>
        <div class="owner-statement-table-separator-1" />
        <div class="px-2 pb-2">
            <div class="grid-container" v-if="reservationsData">
                <span class="pl-3">Reservations</span>
                <grid
                    ref="reservations_grid"
                    class="mt-2"
                    :columnDefs="reservationsColumnDefs"
                    :rowData="reservationsData"
                    :getRowClass="getRowClass"
                    :getRowStyle="getRowStyle"
                    :loading="!isChildrenFetched"
                    :customGridHeight="reservationsDataHeight"
                    :isAutoHeight="reservationsDataHeight == ''"
                    :isSearch="false"
                    grid-theme="ag-theme-alpine-owner-statement"
                    :pinnedBottomRowData="reservationsTotals"
                >
                </grid>
                <div class="owner-statement-table-separator-2" />
            </div>
            <div class="grid-container" v-if="workOrdersData && workOrdersData.length">
                <span class="pl-3">Work Orders</span>
                <grid
                    ref="work_orders_grid"
                    class="mt-2"
                    :columnDefs="workOrdersColumnDefs"
                    :rowData="workOrdersData"
                    :getRowClass="getRowClass"
                    :getRowStyle="getRowStyle"
                    :customGridHeight="workOrderDataHeight"
                    :isAutoHeight="workOrderDataHeight == ''"
                    :isSearch="false"
                    :loading="!isChildrenFetched"
                    grid-theme="ag-theme-alpine-owner-statement"
                    :pinnedBottomRowData="workOrdersTotals"
                    :onCellClicked="onCellClicked"
                >
                </grid>
                <div class="owner-statement-table-separator-2" />
            </div>
            <div class="grid-container" v-if="expensesData && expensesData.length">
                <span class="pl-3">Expenses</span>
                <grid
                    ref="rental_expenses_grid"
                    class="mt-2"
                    :columnDefs="expenseColumnDefs"
                    :rowData="expensesData"
                    :getRowClass="getRowClass"
                    :getRowStyle="getRowStyle"
                    :customGridHeight="expensesDataHeight"
                    :isAutoHeight="expensesDataHeight == ''"
                    :isSearch="false"
                    :loading="!isChildrenFetched"
                    grid-theme="ag-theme-alpine-owner-statement"
                    :pinnedBottomRowData="expensesTotals"
                    :onCellClicked="onCellClicked"
                >
                </grid>
                <div class="owner-statement-table-separator-2" />
            </div>
            <div class="grid-container" style="max-width: 600px;" v-if="totalsData && !isSummary">
                <span class="pl-3">Totals</span>
                <grid
                    ref="totals_grid"
                    class="mt-2"
                    :columnDefs="totalsColumnDefs"
                    :rowData="totalsData"
                    :getRowClass="getRowClass"
                    :getRowStyle="getRowStyle"
                    :overlayNoRowsTemplate="'<span></span>'"
                    :isAutoHeight="true"
                    :isSearch="false"
                    :loading="!isChildrenFetched"
                    grid-theme="ag-theme-alpine-owner-statement-totals"
                    :pinnedBottomRowData="totalsTotal"
                >
                </grid>
                <div class="owner-statement-table-separator-2" />
            </div>
        </div>
        <modal :id="descriptionContentModalId" title="Description" size="lg" ok-title="Close" scrollable ok-only>
            <div style="white-space: pre-wrap;">
                {{ descriptionContent }}
            </div>
        </modal>
    </div>
</template>
<script>
    import grid from '../generic/grid/grid.vue';
    import Modal from '../generic/modal/modal.vue';
    import FormSwitch from '../generic/form-switch/form-switch.vue';
    import ActionsCellRenderer from './additional-tables-actions-cell-renderer.vue';
    import {
        OWNER_STATEMENT_COLUMN_ID,
        OWNER_STATEMENT_COLUMN_RESERVATION_TYPE,
        OWNER_STATEMENT_COLUMN_FROM_DATE,
        OWNER_STATEMENT_COLUMN_NIGHTS,
        OWNER_STATEMENT_COLUMN_GROSS_REVENUE,
        OWNER_STATEMENT_COLUMN_INV_TOTAL,
        OWNER_STATEMENT_COLUMN_RENTAL_FEE,
        OWNER_STATEMENT_COLUMN_CLEANING_FEE,
        OWNER_STATEMENT_COLUMN_OTHER_FEE,
        OWNER_STATEMENT_COLUMN_TOTAL_FEE,
        OWNER_STATEMENT_COLUMN_NET_REVENUE,
        OWNER_STATEMENT_COLUMN_COMM_PERCENT,
        OWNER_STATEMENT_COLUMN_COMMISSION,
        OWNER_STATEMENT_COLUMN_CHANNEL_COMMISSION,
        OWNER_STATEMENT_COLUMN_NET_OWNER,
        OWNER_STATEMENT_COLUMN_MAPPING,
    } from './constants';

    export default {
        components: {
            grid,
            Modal,
            FormSwitch,
            actionsCellRenderer: ActionsCellRenderer,
        },
        data() {
            return {
                descriptionContentModalId: 'description-content-model-id',
            };
        },
        mounted() {
            const reservationsGrid = this.$refs.reservations_grid;
            this.reservationsGridApi = reservationsGrid.gridOptions.api;
            const workOrdersGrid = this.$refs.work_orders_grid;
            this.workOrdersGridApi = workOrdersGrid?.gridOptions.api;
            const rentalExpensesGrid = this.$refs.rental_expenses_grid;
            this.rentalExpensesGridApi = rentalExpensesGrid?.gridOptions.api;
        },
        created() {
            this.isChildrenFetched = this.params.data.is_children_fetched;
            this.ownerStatementStatus = this.params.data.status;
            this.reservationsData = this.params.data?.reservations || [];
            this.reservationTaxesData = this.reservationsData.length > 0 ? this.reservationsData[0].taxes : [];
            this.reservationsTotals = this.params.data?.reservations_totals || [];
            this.workOrdersData = this.params.data?.work_orders || [];
            this.workOrdersTotals = this.params.data?.work_orders_totals || [];
            this.expensesData = this.params.data?.rental_expenses || [];
            this.expensesTotals = this.params.data?.rental_expenses_totals || [];
            this.totalsData = this.params.data?.totals || [];
            this.totalsTotal = this.params.data?.totals_total || [];
            this.isSummary = this.params.data?.property_id || null;
        },
        computed: {
            isPublished() {
                return this.ownerStatementStatus === 'Published';
            },
            reservationsDataHeight() {
                if (this.reservationsData.length === 0) return '200';
                return this.reservationsData.length > 4 ? '253' : '';
            },
            workOrderDataHeight() {
                return this.workOrdersData.length > 4 ? '218' : '';
            },
            expensesDataHeight() {
                return this.expensesData.length > 4 ? '218' : '';
            },
            reservationsColumnDefs() {
                const taxColumns =
                    this.reservationTaxesData.length > 0
                        ? this.reservationTaxesData.map((tax) => {
                              let abbreviatedName = this.abbreviateTaxName(tax.name)

                              return {
                                  field: tax.name,
                                  headerName: abbreviatedName,
                                  valueGetter: (params) => {
                                      const taxes = params.data?.taxes;
                                      if (Array.isArray(taxes)) {
                                          const taxItem = taxes.find((t) => t.name === tax.name);
                                          return taxItem?.value || 0;
                                      }
                                      return 0;
                                  },
                                  minWidth: this.calculateMinWidth(abbreviatedName, 140),
                                  headerClass: 'ag-left-aligned-header',
                                  cellClass: 'ag-left-aligned-cell',
                                  type: 'amountColumn',
                                  sortable: true,
                                  hide: this.isColumnHidden('tax'),
                              };
                          })
                        : [];
                return [
                    {
                        headerName: OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_ID],
                        field: OWNER_STATEMENT_COLUMN_ID,
                        pinned: 'left',
                        minWidth: 85,
                        maxWidth: 85,
                        sortable: true,
                        cellRenderer: ({ data }) => {
                            if (!data || !data.id) return '<span>Totals</span>';
                            if (
                                !['iCal Block', 'Owner Block', 'Lockout Block', 'Block'].includes(data.reservation_type)
                            ) {
                                return `<a href="/app/reservation/edit/invoice/${data.reservation_id}" target="_blank">${data.reservation_id}</a>`;
                            }
                            return `<span>${data.reservation_id}</span>`;
                        },
                        hide: this.isColumnHidden(OWNER_STATEMENT_COLUMN_ID),
                    },
                    {
                        headerName: OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_RESERVATION_TYPE],
                        minWidth: this.calculateMinWidth(
                            OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_RESERVATION_TYPE],
                            100
                        ),
                        maxWidth: 110,
                        field: OWNER_STATEMENT_COLUMN_RESERVATION_TYPE,
                        sortable: true,
                        hide: this.isColumnHidden(OWNER_STATEMENT_COLUMN_RESERVATION_TYPE),
                    },
                    {
                        headerName: OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_FROM_DATE],
                        field: OWNER_STATEMENT_COLUMN_FROM_DATE,
                        type: 'dateColumn',
                        minWidth: this.calculateMinWidth(
                            OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_FROM_DATE],
                            100
                        ),
                        maxWidth: 100,
                        sortable: true,
                        hide: this.isColumnHidden(OWNER_STATEMENT_COLUMN_FROM_DATE),
                    },
                    {
                        headerName: OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_NIGHTS],
                        field: OWNER_STATEMENT_COLUMN_NIGHTS,
                        sortable: true,
                        maxWidth: 70,
                        hide: this.isColumnHidden(OWNER_STATEMENT_COLUMN_NIGHTS),
                        valueGetter: (params) => {
                            return parseInt(params.data?.nights || 0);
                        },
                    },
                    {
                        headerName: OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_GROSS_REVENUE],
                        minWidth: this.calculateMinWidth(
                            OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_GROSS_REVENUE],
                            120
                        ),
                        field: OWNER_STATEMENT_COLUMN_GROSS_REVENUE,
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                        sortable: true,
                        hide: this.isColumnHidden(OWNER_STATEMENT_COLUMN_GROSS_REVENUE),
                    },
                    {
                        headerName: OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_INV_TOTAL],
                        minWidth: this.calculateMinWidth(
                            OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_INV_TOTAL],
                            120
                        ),
                        field: OWNER_STATEMENT_COLUMN_INV_TOTAL,
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                        sortable: true,
                        hide: this.isColumnHidden(OWNER_STATEMENT_COLUMN_INV_TOTAL),
                    },
                    {
                        headerName: 'Taxes',
                        children: [...taxColumns],
                    },
                    {
                        headerName: 'Management Fees',
                        children: [
                            {
                                headerName: OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_RENTAL_FEE],
                                minWidth: this.calculateMinWidth(
                                    OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_RENTAL_FEE],
                                    100
                                ),
                                field: OWNER_STATEMENT_COLUMN_RENTAL_FEE,
                                headerClass: 'ag-left-aligned-header',
                                cellClass: 'ag-left-aligned-cell',
                                type: 'amountColumn',
                                sortable: true,
                                hide: this.isColumnHidden(OWNER_STATEMENT_COLUMN_RENTAL_FEE),
                            },
                            {
                                headerName: OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_CLEANING_FEE],
                                minWidth: this.calculateMinWidth(
                                    OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_CLEANING_FEE],
                                    100
                                ),
                                field: OWNER_STATEMENT_COLUMN_CLEANING_FEE,
                                headerClass: 'ag-left-aligned-header',
                                cellClass: 'ag-left-aligned-cell',
                                type: 'amountColumn',
                                sortable: true,
                                hide: this.isColumnHidden(OWNER_STATEMENT_COLUMN_CLEANING_FEE),
                            },
                            {
                                headerName: OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_OTHER_FEE],
                                minWidth: this.calculateMinWidth(
                                    OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_OTHER_FEE],
                                    100
                                ),
                                field: OWNER_STATEMENT_COLUMN_OTHER_FEE,
                                headerClass: 'ag-left-aligned-header',
                                cellClass: 'ag-left-aligned-cell',
                                type: 'amountColumn',
                                sortable: true,
                                hide: this.isColumnHidden(OWNER_STATEMENT_COLUMN_OTHER_FEE),
                            },
                            {
                                headerName: OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_TOTAL_FEE],
                                minWidth: this.calculateMinWidth(
                                    OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_TOTAL_FEE],
                                    100
                                ),
                                field: OWNER_STATEMENT_COLUMN_TOTAL_FEE,
                                headerClass: 'ag-left-aligned-header',
                                cellClass: 'ag-left-aligned-cell',
                                type: 'amountColumn',
                                sortable: true,
                                hide: this.isColumnHidden(OWNER_STATEMENT_COLUMN_TOTAL_FEE),
                            },
                        ],
                    },
                    {
                        headerName: 'Management Comm',
                        children: [
                            {
                                headerName: OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_COMMISSION],
                                minWidth: this.calculateMinWidth(
                                    OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_COMMISSION],
                                    120
                                ),
                                field: OWNER_STATEMENT_COLUMN_COMMISSION,
                                headerClass: 'ag-left-aligned-header',
                                cellClass: 'ag-left-aligned-cell',
                                type: 'amountColumn',
                                sortable: true,
                                hide: this.isColumnHidden(OWNER_STATEMENT_COLUMN_COMMISSION),
                            },
                            {
                                headerName: OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_COMM_PERCENT],
                                minWidth: this.calculateMinWidth(
                                    OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_COMM_PERCENT],
                                    100
                                ),
                                maxWidth: 80,
                                field: OWNER_STATEMENT_COLUMN_COMM_PERCENT,
                                headerClass: 'ag-left-aligned-header',
                                cellClass: 'ag-left-aligned-cell',
                                type: 'amountColumn',
                                cellRendererParams: {
                                    percentage: true,
                                },
                                sortable: true,
                                hide: this.isColumnHidden(OWNER_STATEMENT_COLUMN_COMM_PERCENT),
                            },
                        ],
                    },
                    {
                        headerName: OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_CHANNEL_COMMISSION],
                        minWidth: this.calculateMinWidth(
                            OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_CHANNEL_COMMISSION],
                            100
                        ),
                        field: OWNER_STATEMENT_COLUMN_CHANNEL_COMMISSION,
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                        sortable: true,
                        hide: this.isColumnHidden(OWNER_STATEMENT_COLUMN_CHANNEL_COMMISSION),
                    },
                    {
                        headerName: OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_NET_REVENUE],
                        minWidth: this.calculateMinWidth(
                            OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_NET_REVENUE],
                            100
                        ),
                        field: OWNER_STATEMENT_COLUMN_NET_REVENUE,
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                        sortable: true,
                        hide: this.isColumnHidden(OWNER_STATEMENT_COLUMN_NET_REVENUE),
                    },
                    {
                        headerName: OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_NET_OWNER],
                        minWidth: this.calculateMinWidth(
                            OWNER_STATEMENT_COLUMN_MAPPING[OWNER_STATEMENT_COLUMN_NET_OWNER],
                            100
                        ),
                        field: OWNER_STATEMENT_COLUMN_NET_OWNER,
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                        sortable: true,
                        hide: this.isColumnHidden(OWNER_STATEMENT_COLUMN_NET_OWNER),
                    },
                    {
                        headerName: '',
                        minWidth: 55,
                        maxWidth: 55,
                        pinned: 'right',
                        field: 'reservations_action',
                        cellRenderer: 'actionsCellRenderer',
                        cellRendererParams: {
                            onDeleteReservationSuccess: this.onDeleteReservationSuccess,
                        },
                        hide: this.isPublished || this.isSummary,
                    },
                ];
            },
            workOrdersColumnDefs() {
                return [
                    {
                        headerName: 'ID',
                        field: 'work_order_number',
                        sortable: true,
                        cellRenderer: ({ data }) => {
                            return data && data.id
                                ? `<a href="/app/work-orders/list/?work_order_id=${data.work_order_id}" target="_blank">${data.work_order_number}</a>`
                                : '<span>Totals</span>';
                        },
                    },
                    {
                        headerName: 'Description',
                        cellClassRules: {
                            'lx-clickable-cell text-primary cursor-pointer': params => params.data && params.data.id,
                        },
                        field: 'description',
                        sortable: true,
                    },
                    {
                        headerName: 'Completed',
                        field: 'completed',
                        type: 'dateColumn',
                        sortable: true,
                    },
                    {
                        headerName: 'Amount',
                        field: 'amount',
                        sortable: true,
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                    },
                    {
                        headerName: '',
                        minWidth: 55,
                        maxWidth: 55,
                        field: 'work_order_action',
                        cellRenderer: 'actionsCellRenderer',
                        cellRendererParams: {
                            onDeleteWorkOrderSuccess: this.onDeleteWorkOrderSuccess,
                        },
                        hide: this.isPublished || this.isSummary,
                    },
                ];
            },
            expenseColumnDefs() {
                return [
                    {
                        headerName: 'ID',
                        field: 'rental_expense_id',
                        sortable: true,
                        cellRenderer: ({ data }) => {
                            return data && data.id
                                ? `<a href="/app/rental-expense/list/?rental_expense_id=${data.rental_expense_id}" target="_blank">${data.rental_expense_id}</a>`
                                : '<span>Totals</span>';
                        },
                    },
                    {
                        headerName: 'Description',
                        cellClassRules: {
                            'lx-clickable-cell text-primary cursor-pointer': params => params.data && params.data.id,
                        },
                        field: 'description',
                        sortable: true,
                    },
                    {
                        headerName: 'Entered',
                        field: 'entered',
                        type: 'dateColumn',
                        sortable: true,
                    },
                    {
                        headerName: 'Amount',
                        field: 'amount',
                        sortable: true,
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                    },
                    {
                        headerName: '',
                        minWidth: 55,
                        maxWidth: 55,
                        field: 'expense_action',
                        cellRenderer: 'actionsCellRenderer',
                        cellRendererParams: {
                            onDeleteRentalExpenseSuccess: this.onDeleteRentalExpenseSuccess,
                        },
                        hide: this.isPublished || this.isSummary,
                    },
                ];
            },
            totalsColumnDefs() {
                return [
                    {
                        headerName: '',
                        field: 'description',
                        pinned: 'left',
                        minWidth: 300,
                        maxWidth: 300,
                        cellRenderer: ({ data }) => {
                            return data && data.description
                                ? data.description
                                : '<span>Total Balance due Owner:</span>';
                        },
                        sortable: true,
                    },
                    {
                        headerName: '',
                        field: 'amount',
                        sortable: true,
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                    },
                ];
            },
        },
        methods: {
            abbreviateTaxName(taxName) {
                let cleanedName = taxName.replace(/tax/i, '').trim();

                // Extract trailing parenthesized part (if any)
                let postfix = '';
                let mainPart = cleanedName;
                const match = cleanedName.match(/\s*(\([^)]*\))$/);
                if (match) {
                    postfix = match[1];
                    mainPart = cleanedName.substring(0, match.index).trim();
                }

                // Abbreviate mainPart if it has more than two words
                let words = mainPart.split(/\s+/);
                let abbreviatedMainPart = words.length > 2 ? words.map(word => word[0].toUpperCase()).join('') : mainPart;

                // Append the postfix (if it exists) without abbreviating it
                let abbreviatedName = postfix ? abbreviatedMainPart + ' ' + postfix : abbreviatedMainPart;

                return abbreviatedName
            },
            calculateMinWidth(headerName, minWidth) {
                const avgCharWidth = 6;
                const padding = 0;
                const maxCharsPerLine = 10;
                const totalLines = Math.ceil(headerName.length / maxCharsPerLine);
                const effectiveLines = Math.min(totalLines, 2);
                return Math.max(avgCharWidth * maxCharsPerLine * effectiveLines + padding, minWidth);
            },
            onDeleteReservationSuccess(data) {
                this.successToast({ text: `Reservation record successfully deleted.` });
                this.params.updateOwnerStatement(data);
            },
            onDeleteWorkOrderSuccess(data) {
                this.successToast({ text: `Work order record successfully deleted.` });
                this.params.updateOwnerStatement(data);
            },
            onDeleteRentalExpenseSuccess(data) {
                this.successToast({ text: `Rental expense record successfully deleted.` });
                this.params.updateOwnerStatement(data);
            },
            onCellClicked({ column, data }) {
                if (column.colId === 'description' && data && data.id) {
                    this.descriptionContent = data.description;
                    this.$bvModal.show(this.descriptionContentModalId);
                }
            },
            getRowClass() {
                return 'lx-additional-tables-row';
            },
            getRowStyle(params) {
                if (params.node.rowPinned === 'bottom') {
                    return { 'font-weight': 'bold' };
                }
            },
            isColumnHidden(column) {
                const columnSetting = this.params.columnSettings.find(
                    (columnSetting) => columnSetting.field === column
                );
                if (!columnSetting) return false;
                return !columnSetting.visible;
            },
        },
    };
</script>
<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    div.owner-statement-table-separator-1 {
        border: 0;
        height: 2px;
        margin: 0 0px 20px 0px;
    }
    div.owner-statement-table-separator-2 {
        border: 0;
        height: 2px;
        background: rgba(29, 174, 247, 0.2);
        box-shadow: 0 2px 5px rgba(29, 174, 247, 0.5);
        margin: 0 3px 20px 3px;
    }
    div.grid-container {
        padding: 0 40px 0 40px;
        @include media-breakpoint-down(md) {
            padding: 0;
        }
    }
</style>
